import { createContext, useContext, useState, useEffect, useMemo } from "react";

const LanguageContext = createContext(null);

export const LANGUAGES = [
  { code: "en", name: "English", dir: "ltr" },
  { code: "ar", name: "العربية", dir: "rtl" },
  { code: "sw", name: "Kiswahili", dir: "ltr" },
  { code: "fr", name: "Français", dir: "ltr" },
  { code: "pt", name: "Português", dir: "ltr" }
];

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("af_lang") || "en");

  useEffect(() => {
    localStorage.setItem("af_lang", lang);
    const langObj = LANGUAGES.find(l => l.code === lang);
    document.documentElement.dir = langObj?.dir || "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  const langValue = useMemo(() => ({ lang, setLang }), [lang]);

  return (
    <LanguageContext.Provider value={langValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be inside LanguageProvider");
  return ctx;
}

export function useTranslation() {
  const { lang } = useLanguage();
  const t = (key) => {
    const keys = key.split(".");
    let val = translations[lang];
    for (const k of keys) {
      val = val?.[k];
    }
    return val || translations["en"]?.[keys[0]]?.[keys[1]] || key;
  };
  return { t, lang };
}

const translations = {
  en: {
    nav: {
      vision: "Our Vision",
      why: "Why This Matters",
      about: "About Us",
      support: "Support Us",
      survey: "Take the Survey",
      home: "Home"
    },
    hero: {
      overline: "United by Passion, Powered by Pride",
      headline1: "The Voice of",
      headline2: "African Football Fans",
      subtitle: "Afro Fanthem celebrates, counts, and amplifies African football fans through music, stories, surveys, and community debate.",
      cta: "Take the Survey",
      ctaDebate: "Join the Debate",
      ctaVision: "Our Vision",
      nations: "African Nations",
      fans: "Potential Fans",
      voice: "United Voice"
    },
    why: {
      overline: "Why This Matters",
      headline1: "One Third of Premier League",
      headline2: "Viewers Are African",
      p1: "English football has the highest global viewership, and African fans represent a significant portion of that audience. Yet we remain underrepresented in club engagement strategies, membership programs, and supporter recognition.",
      p2: "Many of us may never attend a match at Anfield, Old Trafford, or the Emirates, but our loyalty, passion, and emotional commitment to our clubs is as real as any fan in the stands.",
      p3: "This campaign aims to demonstrate that African fans are integral to Premier League club culture and deserve meaningful inclusion in the global football community.",
      cta: "Add Your Voice"
    },
    demands: {
      overline: "What African Fans Want",
      headline: "Our Vision",
      subtitle: "These are the key areas where African fans see opportunities for clubs to deepen engagement and build stronger, more meaningful relationships with their global supporter base.",
      cta: "Share Your Voice",
      items: [
        { title: "Local Access to Club Experiences", desc: "African fans value opportunities to experience clubs in-market through tours, activations, and pre-season engagements." },
        { title: "Recognition as Global Supporters", desc: "Fans want to feel recognised as part of the club's global community, not just distant audiences." },
        { title: "Access to Official Products", desc: "There is strong demand for accessible, authentic club merchandise within local markets." },
        { title: "Participation, Not Just Viewership", desc: "One-way communication is no longer enough. Fans want a two-way relationship where they can engage, contribute, and feel involved." },
        { title: "Value Beyond Viewership", desc: "Supporters want their loyalty to translate into meaningful connection, access, and benefits." },
        { title: "Accessible Membership Models", desc: "Clear interest in structured membership options tailored to international fans, with accessible pricing and relevant benefits." },
        { title: "Localised Payment Options", desc: "Fans require payment methods that reflect how transactions happen in African markets, including mobile money and hybrid systems." },
        { title: "Direct and Meaningful Communication", desc: "Supporters value more direct, relevant, and personalised communication beyond generic global messaging." },
        { title: "Community and Fan Networks", desc: "Strong appetite for organised fan communities that connect supporters locally and across regions." },
        { title: "Trusted and Consistent Access", desc: "Fans want reliable, official channels to engage with clubs, reducing reliance on informal or counterfeit markets." }
      ]
    },
    keynote: {
      title: "A Message of Respect & Unity",
      p1title: "As non-resident fans, we have great respect for match-going supporters.",
      p1: " They are the true heartbeat of the club, the live spark that brings energy and passion to the players on the pitch and creates the special atmosphere inside the stadium.",
      p2title: "We are not here to replace them.",
      p2: " We simply want to be recognised as a valued part of the wider fan community from afar. We believe we deserve greater alignment and inclusion in conversations with the clubs and local fans.",
      p3title: "We strongly believe in harmonious co-existence.",
      p3: " It was the dedicated support of fans around the world, through television rights, that helped spread these clubs' football globally. This exposure inspired talented players from our countries to join the leagues and elevated the game for everyone. The relationship has always been mutually beneficial.",
      conclusion: "We are confident that the time has come to take this relationship between the clubs, their local supporters, and the international fan base to the next level — built on mutual respect and genuine inclusion."
    },
    prize: {
      title: "Win Exclusive Club Merchandise",
      subtitle: "100 winners selected at random. Complete the survey with a phone number to enter.",
      cta: "Take the Survey",
      endsIn: "Ends in:"
    },
    supportSection: {
      overline: "Power the Movement",
      headline: "Support Afro Fanthem",
      subtitle: "By following, subscribing, streaming, and donating, you directly support the project financially and help grow the movement. Every action counts."
    },
    finalCta: {
      headline1: "Ready to Make",
      headline2: "Your Voice Heard?",
      subtitle: "Join thousands of African football fans in the conversation with Premier League clubs. The survey takes 5 minutes but adds to a powerful collective voice.",
      cta: "Take the Survey"
    },
    footer: {
      tagline: "United by Passion, Powered by Pride",
      privacy: "Privacy Policy",
      about: "About Us",
      rights: "Afro Fanthem. All rights reserved."
    },
    survey: {
      headline: "The African Fan Voice",
      subtitle: "This survey represents the voice of African football fans globally. Your responses will help shape how clubs engage with fans across Africa.",
      step: "Step",
      of: "of",
      saving: "Saving...",
      previous: "Previous",
      next: "Next",
      submit: "Submit Survey",
      completeRegister: "Complete & Register",
      prizeTitle: "Prize Draw Entry",
      prizeSubtitle: "Optional - Enter to win exclusive merchandise",
      prizeInfo: "At the end of August, we'll randomly select 100 fans to win merchandise from their favorite club. Phone number is required to verify location and prevent fraud.",
      prizeQ: "Would you like to enter the prize draw?",
      prizeYes: "Yes, I want to enter",
      prizeNo: "No, skip the prize draw",
      emailPrize: "Email for Prize Entry",
      phonePrize: "Phone Number",
      phoneNote: "Include country code. Required for location verification.",
      fanProfile: "Fan Profile",
      country: "Country",
      city: "City",
      ageGroup: "Age Group",
      followFreq: "How often do you follow your club?",
      clubSupport: "Club Support",
      clubQ: "Which club do you support?",
      clubOther: "Please specify your club",
      duration: "How long have you supported this club?",
      engagement: "Engagement Today",
      engageQ: "How do you currently engage with your club?",
      selectAll: "Select all that apply",
      recognised: "Do you feel recognised or valued by your club?",
      doEnough: "Do you believe clubs do enough to engage fans in Africa?",
      connection: "How connected do you feel to your club?",
      gap: "The Gap",
      gapQ: "What prevents you from engaging more with your club?",
      future: "Future Opportunity",
      memberQ: "If your club offered an international membership for African fans, would you be interested?",
      memberValues: "What would you value most in a membership?",
      selectUp: "Select up to 3 options",
      selected: "selected",
      priceQ: "How much would you be willing to pay monthly?",
      voice: "Your Voice",
      voiceIntro: "This is your chance to speak directly to the clubs. Your response will be shared (anonymously) with football institutions as part of our advocacy.",
      voiceQ: "In your own words, what could your club do better to make you feel more connected?",
      voiceNote: "Optional, but your voice matters!",
      winners: "100 Winners Selected!"
    },
    register: {
      finalStep: "Final Step",
      step1: "Step 1 of 2",
      headlineValidate: "Validate Your Voice",
      headlineJoin: "Join the Movement",
      subtitleValidate: "Register your email to validate your survey response. We'll send a quick verification link.",
      subtitleJoin: "Register to add your voice to the African fan movement. After registration, you'll complete a short survey.",
      trust: "We validate every response to ensure authentic African fan voices are heard. Your data is protected and never shared.",
      whyVerify: "Why we verify:",
      email: "Email Address",
      phone: "Phone Number",
      phoneNote: "Phone number is required for prize draw eligibility. Include country code for verification.",
      prizeInfo: "100 fans who complete the survey with a valid phone number will be selected at the end of August to win merchandise from their favorite club.",
      prizeTitle: "Win Exclusive Merchandise!",
      consent: "I agree to the storage and processing of my data for the purposes of this campaign. I understand my information may be used to contact me if I win the prize draw.",
      privacyLink: "View Privacy Policy",
      submitValidate: "Verify & Submit",
      submitContinue: "Continue to Survey",
      sending: "Sending Verification...",
      registering: "Registering...",
      already: "Already registered?",
      returnHome: "Return to homepage",
      optional: "(optional)",
      required: "*",
      referralBanner: "You were invited by a fellow fan! Complete the survey with a phone number and you'll both get extra prize draw entries.",
      checkEmail: "Check Your Email",
      checkEmailMsg: "Click the link in the email to verify your address and validate your survey response. If you provided a phone number, you'll be entered into the prize draw once verified.",
      didntReceive: "Didn't receive the email? Check your spam folder or",
      tryAgain: "try again with a different email"
    },
    verify: {
      verifying: "Verifying your email...",
      failed: "Verification Failed",
      returnHome: "Return Home",
      alreadyVerified: "Already Verified!",
      verified: "Email Verified!",
      subtitle: "Your voice has been validated and added to the movement.",
      subtitle2: "Together, we're building a credible case for fan representation and inclusion.",
      spread: "Spread the Word",
      spreadSub: "Every voice strengthens the movement. Share with your friends and community!",
      whatsapp: "Share on WhatsApp",
      shareOther: "Share via Other",
      earnTitle: "Earn Extra Prize Entries",
      earnSub: "Share your unique link. When friends complete the survey with a phone number, you both get an extra prize draw entry!",
      copy: "Copy",
      backHome: "Return to Homepage"
    },
    about: {
      headline: "About",
      headlineHighlight: "Afro Fanthem",
      takeSurvey: "Take Survey",
      home: "Home",
      s1Title: "About Afro Fanthem",
      s1p1: "Afro Fanthem is a collective of African fans, working together and collaborating with fan engagement platforms and startups to build stronger, more inclusive relationships between clubs and supporters around the world.",
      s1p2: "We represent fans who love the game deeply, even when they are far from it.",
      s2Title: "Why We Exist",
      s2p1: "Across Africa, millions of fans show up every single week.",
      s2p2: "We wake up early. We stay up late. We celebrate every goal and carry every loss.",
      s2p3: "But for most of us, the reality is simple. We will never set foot in a stadium. Not because we do not care enough. But because of distance, cost, and access.",
      s2p4: "And because of that, we are often made to feel like we do not fully belong. We feel alienated. Unheard. As if our presence does not count.",
      s2p5: "Afro Fanthem exists to change that.",
      s3Title: "What Recognition Means",
      s3p1: "Recognition is not just about being seen. It means being included in a real way.",
      s3p2: "It means access to merchandise without barriers. It means access to club culture and experiences. It means being counted when decisions are made.",
      s3p3: "It means meaningful international membership for fans who may never reach the stadium, but are still part of the club every day.",
      s3p4: "It means knowing that distance does not make you less of a fan.",
      s4Title: "What We Are Building",
      s4p1: "We are building a unified African fan voice. Not to replace anyone. But to stand alongside fans everywhere.",
      s4p2: "Because this is not about taking space from others. It is about making sure we are not left out of it.",
      s5Title: "How You Can Be Part of It",
      s5p1: "The simplest way to be part of this is to make your voice count.",
      s5p2: "Fill in the survey. Share it with your community. Follow and engage with the movement.",
      s5p3: "Every response matters. Every voice adds strength.",
      s6Title: "The Goal",
      s6p1: "By the 2026/27 season, we want African fans to be recognised as a critical part of the global fan conversation.",
      s6p2: "Not just present. But included. Valued. Heard.",
      joinHeadline: "Join the Movement",
      joinP1: "If you have ever supported a club from a distance",
      joinP2: "If you have ever felt overlooked as a fan",
      joinP3: "If you have ever wondered if your voice matters",
      joinCta: "This is for you.",
      joinButton: "Take the Survey"
    },
    thankyou: {
      headline: "Thank You!",
      subtitle: "Your voice has been added to the movement.",
      subtitle2: "Together, we're building a credible case for fan representation and inclusion.",
      prizeTitle: "You're in the Prize Draw!",
      earnTitle: "Earn Extra Entries",
      earnSub: "Share your unique link with friends. When they complete the survey with a phone number, you both get an extra prize draw entry!",
      supportTitle: "Power the Movement",
      supportSub: "By following, subscribing, streaming, and donating, you directly support the project financially and help grow the movement. Every action counts.",
      sharePrompt: "Help us spread the word!",
      whatsapp: "Share on WhatsApp",
      shareOther: "Share via Other",
      backHome: "Return to Homepage"
    }
  },
  ar: {
    nav: { vision: "رؤيتنا", why: "لماذا هذا مهم", about: "من نحن", support: "ادعمنا", survey: "شارك في الاستبيان", home: "الرئيسية" },
    hero: {
      overline: "متحدون بالشغف، مدعومون بالفخر",
      headline1: "صوت",
      headline2: "مشجعي كرة القدم الأفارقة",
      subtitle: "نحن أكثر من مجرد مشاهدين. نحن مشجعون نستحق الاعتراف والاندماج ومكاناً على الطاولة. انضم إلى الحركة لإسماع الأصوات الأفريقية لأكبر أندية كرة القدم في العالم.",
      cta: "شارك في الاستبيان",
      ctaVision: "رؤيتنا",
      nations: "دولة أفريقية",
      fans: "مشجع محتمل",
      voice: "صوت واحد"
    },
    why: {
      overline: "لماذا هذا مهم",
      headline1: "ثلث مشاهدي الدوري الإنجليزي",
      headline2: "هم أفارقة",
      p1: "كرة القدم الإنجليزية لديها أعلى نسبة مشاهدة عالمية، والمشجعون الأفارقة يمثلون جزءاً كبيراً من هذا الجمهور. ومع ذلك نبقى غير ممثلين بشكل كافٍ في استراتيجيات التفاعل والعضويات والاعتراف بالمشجعين.",
      p2: "قد لا يحضر الكثير منا مباراة في أنفيلد أو أولد ترافورد أو الإمارات، لكن ولاءنا وشغفنا والتزامنا العاطفي بأنديتنا حقيقي كأي مشجع في المدرجات.",
      p3: "تهدف هذه الحملة إلى إثبات أن المشجعين الأفارقة جزء لا يتجزأ من ثقافة أندية الدوري الإنجليزي ويستحقون الاندماج الحقيقي في مجتمع كرة القدم العالمي.",
      cta: "أضف صوتك"
    },
    demands: {
      overline: "ما يريده المشجعون الأفارقة",
      headline: "رؤيتنا",
      subtitle: "هذه هي المجالات الرئيسية التي يرى فيها المشجعون الأفارقة فرصاً للأندية لتعميق التفاعل وبناء علاقات أقوى وأكثر معنى مع قاعدة مشجعيها العالمية.",
      cta: "شارك بصوتك",
      items: [
        { title: "الوصول المحلي لتجارب الأندية", desc: "يقدر المشجعون الأفارقة فرص تجربة الأندية من خلال الجولات والفعاليات والمباريات التحضيرية." },
        { title: "الاعتراف كمشجعين عالميين", desc: "يريد المشجعون أن يشعروا بالاعتراف بهم كجزء من مجتمع النادي العالمي." },
        { title: "الوصول إلى المنتجات الرسمية", desc: "هناك طلب قوي على بضائع الأندية الأصلية في الأسواق المحلية." },
        { title: "المشاركة وليس المشاهدة فقط", desc: "التواصل أحادي الاتجاه لم يعد كافياً. يريد المشجعون علاقة تفاعلية." },
        { title: "قيمة تتجاوز المشاهدة", desc: "يريد المشجعون أن يتحول ولاؤهم إلى اتصال حقيقي وفوائد." },
        { title: "نماذج عضوية ميسرة", desc: "اهتمام واضح بخيارات عضوية مصممة للمشجعين الدوليين بأسعار معقولة." },
        { title: "خيارات دفع محلية", desc: "يحتاج المشجعون إلى طرق دفع تعكس كيفية إجراء المعاملات في الأسواق الأفريقية." },
        { title: "تواصل مباشر وهادف", desc: "يقدر المشجعون التواصل المباشر والشخصي بدلاً من الرسائل العامة." },
        { title: "المجتمع وشبكات المشجعين", desc: "رغبة قوية في مجتمعات منظمة تربط المشجعين محلياً وعبر المناطق." },
        { title: "وصول موثوق ومتسق", desc: "يريد المشجعون قنوات رسمية وموثوقة للتفاعل مع الأندية." }
      ]
    },
    keynote: {
      title: "رسالة احترام ووحدة",
      p1title: "كمشجعين غير مقيمين، لدينا احترام كبير للمشجعين الحاضرين.",
      p1: " هم نبض النادي الحقيقي، الشرارة الحية التي تجلب الطاقة والشغف للاعبين على أرض الملعب.",
      p2title: "نحن لسنا هنا لاستبدالهم.",
      p2: " نريد ببساطة أن نُعترف بنا كجزء قيّم من مجتمع المشجعين الأوسع.",
      p3title: "نؤمن بشدة بالتعايش المتناغم.",
      p3: " لقد ساعد دعم المشجعين حول العالم عبر حقوق البث في نشر كرة القدم عالمياً.",
      conclusion: "نحن واثقون أن الوقت قد حان للارتقاء بهذه العلاقة إلى المستوى التالي — مبنية على الاحترام المتبادل والاندماج الحقيقي."
    },
    prize: { title: "اربح بضائع حصرية", subtitle: "100 فائز يتم اختيارهم عشوائياً. أكمل الاستبيان مع رقم هاتف للمشاركة.", cta: "شارك في الاستبيان", endsIn: "ينتهي في:" },
    supportSection: { overline: "ادعم الحركة", headline: "ادعم أفرو فانثيم", subtitle: "من خلال المتابعة والاشتراك والبث والتبرع، تدعم المشروع مالياً وتساعد في نمو الحركة." },
    finalCta: { headline1: "مستعد لإسماع", headline2: "صوتك؟", subtitle: "انضم إلى آلاف المشجعين الأفارقة في الحوار مع أندية الدوري الإنجليزي. الاستبيان يستغرق 5 دقائق.", cta: "شارك في الاستبيان" },
    footer: { tagline: "متحدون بالشغف، مدعومون بالفخر", privacy: "سياسة الخصوصية", about: "من نحن", rights: "أفرو فانثيم. جميع الحقوق محفوظة." },
    survey: {
      headline: "صوت المشجع الأفريقي",
      subtitle: "يمثل هذا الاستبيان صوت مشجعي كرة القدم الأفارقة عالمياً. ستساعد إجاباتك في تشكيل كيفية تفاعل الأندية مع المشجعين في أفريقيا.",
      step: "الخطوة", of: "من", saving: "جاري الحفظ...", previous: "السابق", next: "التالي",
      submit: "إرسال الاستبيان", completeRegister: "أكمل وسجل",
      prizeTitle: "المشاركة في السحب", prizeSubtitle: "اختياري - شارك للفوز ببضائع حصرية",
      prizeInfo: "في نهاية أغسطس، سنختار عشوائياً 100 مشجع للفوز ببضائع من ناديهم المفضل. رقم الهاتف مطلوب للتحقق.",
      prizeQ: "هل تريد المشاركة في السحب؟", prizeYes: "نعم، أريد المشاركة", prizeNo: "لا، تخطي السحب",
      emailPrize: "البريد الإلكتروني للمشاركة", phonePrize: "رقم الهاتف", phoneNote: "أضف رمز الدولة. مطلوب للتحقق من الموقع.",
      fanProfile: "ملف المشجع", country: "الدولة", city: "المدينة", ageGroup: "الفئة العمرية", followFreq: "كم مرة تتابع ناديك؟",
      clubSupport: "دعم النادي", clubQ: "أي نادٍ تشجع؟", clubOther: "حدد ناديك", duration: "منذ متى تشجع هذا النادي؟",
      engagement: "التفاعل الحالي", engageQ: "كيف تتفاعل حالياً مع ناديك؟", selectAll: "اختر كل ما ينطبق",
      recognised: "هل تشعر بالاعتراف بك من ناديك؟", doEnough: "هل تعتقد أن الأندية تفعل ما يكفي لإشراك المشجعين في أفريقيا؟",
      connection: "ما مدى شعورك بالارتباط بناديك؟",
      gap: "الفجوة", gapQ: "ما الذي يمنعك من التفاعل أكثر مع ناديك؟",
      future: "فرصة المستقبل", memberQ: "إذا عرض ناديك عضوية دولية للمشجعين الأفارقة، هل ستكون مهتماً؟",
      memberValues: "ما أكثر ما تقدره في العضوية؟", selectUp: "اختر حتى 3 خيارات", selected: "مختار",
      priceQ: "كم ستكون مستعداً لدفعه شهرياً؟",
      voice: "صوتك", voiceIntro: "هذه فرصتك للتحدث مباشرة إلى الأندية. سيتم مشاركة ردك (مجهول الهوية) مع المؤسسات الكروية.",
      voiceQ: "بكلماتك الخاصة، ماذا يمكن لناديك فعله بشكل أفضل لتشعر بمزيد من الارتباط؟",
      voiceNote: "اختياري، لكن صوتك مهم!", winners: "100 فائز سيتم اختيارهم!"
    },
    register: {
      finalStep: "الخطوة الأخيرة", step1: "الخطوة 1 من 2",
      headlineValidate: "صادق على صوتك", headlineJoin: "انضم إلى الحركة",
      subtitleValidate: "سجل بريدك الإلكتروني للتحقق من إجابتك. سنرسل رابط تحقق سريع.",
      subtitleJoin: "سجل لإضافة صوتك. بعد التسجيل، ستكمل استبياناً قصيراً.",
      trust: "نتحقق من كل إجابة لضمان أصالة أصوات المشجعين الأفارقة. بياناتك محمية ولن تتم مشاركتها.",
      whyVerify: "لماذا نتحقق:", email: "البريد الإلكتروني", phone: "رقم الهاتف",
      phoneNote: "رقم الهاتف مطلوب للمشاركة في السحب. أضف رمز الدولة للتحقق.",
      prizeInfo: "100 مشجع أكملوا الاستبيان مع رقم هاتف صالح سيتم اختيارهم في نهاية أغسطس للفوز ببضائع من ناديهم المفضل.",
      prizeTitle: "اربح بضائع حصرية!",
      consent: "أوافق على تخزين ومعالجة بياناتي لأغراض هذه الحملة. أفهم أنه قد يتم استخدام معلوماتي للتواصل معي إذا فزت.",
      privacyLink: "عرض سياسة الخصوصية", submitValidate: "تحقق وأرسل", submitContinue: "المتابعة إلى الاستبيان",
      sending: "جاري إرسال التحقق...", registering: "جاري التسجيل...", already: "مسجل بالفعل؟", returnHome: "العودة للرئيسية",
      optional: "(اختياري)", required: "*",
      referralBanner: "تمت دعوتك من مشجع آخر! أكمل الاستبيان مع رقم هاتف وستحصلان على فرص إضافية في السحب.",
      checkEmail: "تحقق من بريدك", checkEmailMsg: "انقر على الرابط في البريد للتحقق من عنوانك.",
      didntReceive: "لم تستلم البريد؟ تحقق من مجلد الرسائل غير المرغوبة أو", tryAgain: "حاول مرة أخرى ببريد مختلف"
    },
    verify: {
      verifying: "جاري التحقق من بريدك...", failed: "فشل التحقق", returnHome: "العودة للرئيسية",
      alreadyVerified: "تم التحقق بالفعل!", verified: "تم التحقق من البريد!",
      subtitle: "تم التحقق من صوتك وإضافته إلى الحركة.", subtitle2: "معاً، نبني حجة موثوقة للتمثيل والاندماج.",
      spread: "انشر الكلمة", spreadSub: "كل صوت يقوي الحركة. شارك مع أصدقائك!",
      whatsapp: "شارك على واتساب", shareOther: "شارك عبر أخرى",
      earnTitle: "اربح فرص سحب إضافية", earnSub: "شارك رابطك الفريد. عندما يكمل الأصدقاء الاستبيان، تحصلان على فرصة إضافية!",
      copy: "نسخ", backHome: "العودة للرئيسية"
    },
    about: {
      headline: "عن", headlineHighlight: "أفرو فانثيم", takeSurvey: "شارك في الاستبيان", home: "الرئيسية",
      s1Title: "عن أفرو فانثيم",
      s1p1: "أفرو فانثيم هو تجمع من المشجعين الأفارقة، يعملون معاً ويتعاونون مع منصات التفاعل مع المشجعين لبناء علاقات أقوى وأكثر شمولاً بين الأندية والمشجعين حول العالم.",
      s1p2: "نحن نمثل المشجعين الذين يحبون اللعبة بعمق، حتى عندما يكونون بعيدين عنها.",
      s2Title: "لماذا نحن موجودون",
      s2p1: "في جميع أنحاء أفريقيا، يحضر ملايين المشجعين كل أسبوع.",
      s2p2: "نستيقظ مبكراً. نسهر حتى وقت متأخر. نحتفل بكل هدف ونتحمل كل خسارة.",
      s2p3: "لكن بالنسبة لمعظمنا، الواقع بسيط. لن نطأ ملعباً أبداً. ليس لأننا لا نهتم بما يكفي. ولكن بسبب المسافة والتكلفة والوصول.",
      s2p4: "وبسبب ذلك، غالباً ما نشعر بأننا لا ننتمي بالكامل. نشعر بالاغتراب. غير مسموعين.",
      s2p5: "أفرو فانثيم موجود لتغيير ذلك.",
      s3Title: "ماذا يعني الاعتراف",
      s3p1: "الاعتراف ليس مجرد أن تُرى. إنه يعني أن تُدرج بطريقة حقيقية.",
      s3p2: "يعني الوصول إلى البضائع بدون حواجز. يعني الوصول إلى ثقافة النادي. يعني أن يُحسب لك عند اتخاذ القرارات.",
      s3p3: "يعني عضوية دولية هادفة للمشجعين الذين قد لا يصلون إلى الملعب أبداً، لكنهم جزء من النادي كل يوم.",
      s3p4: "يعني أن تعرف أن المسافة لا تجعلك أقل كمشجع.",
      s4Title: "ما نبنيه",
      s4p1: "نحن نبني صوتاً أفريقياً موحداً للمشجعين. ليس لاستبدال أحد. بل للوقوف جنباً إلى جنب مع المشجعين في كل مكان.",
      s4p2: "لأن هذا ليس عن أخذ مساحة من الآخرين. إنه عن التأكد من أننا لسنا مستبعدين منها.",
      s5Title: "كيف تكون جزءاً منها",
      s5p1: "أبسط طريقة لتكون جزءاً من هذا هي أن تجعل صوتك مسموعاً.",
      s5p2: "املأ الاستبيان. شاركه مع مجتمعك. تابع وتفاعل مع الحركة.",
      s5p3: "كل إجابة مهمة. كل صوت يضيف قوة.",
      s6Title: "الهدف",
      s6p1: "بحلول موسم 2026/27، نريد أن يُعترف بالمشجعين الأفارقة كجزء حاسم من حوار المشجعين العالمي.",
      s6p2: "ليسوا حاضرين فقط. بل مُدرجين. مُقدَّرين. مسموعين.",
      joinHeadline: "انضم إلى الحركة",
      joinP1: "إذا كنت قد شجعت نادياً من بعيد", joinP2: "إذا شعرت يوماً بالتجاهل كمشجع",
      joinP3: "إذا تساءلت يوماً عما إذا كان صوتك مهماً", joinCta: "هذا لأجلك.", joinButton: "شارك في الاستبيان"
    },
    thankyou: {
      headline: "شكراً لك!", subtitle: "تمت إضافة صوتك إلى الحركة.", subtitle2: "معاً نبني حجة للتمثيل والاندماج.",
      prizeTitle: "أنت في السحب!", earnTitle: "اربح فرصاً إضافية",
      earnSub: "شارك رابطك الفريد مع الأصدقاء.",
      supportTitle: "ادعم الحركة", supportSub: "بالمتابعة والاشتراك والتبرع، تدعم المشروع.",
      sharePrompt: "ساعدنا في نشر الكلمة!", whatsapp: "شارك على واتساب", shareOther: "شارك عبر أخرى", backHome: "العودة للرئيسية"
    }
  },
  sw: {
    nav: { vision: "Maono Yetu", why: "Kwa Nini Hii ni Muhimu", about: "Kuhusu Sisi", support: "Tuunge Mkono", survey: "Jibu Utafiti", home: "Nyumbani" },
    hero: {
      overline: "Tumeungana kwa Shauku, Tunaendeshwa na Kiburi",
      headline1: "Sauti ya",
      headline2: "Mashabiki wa Mpira Afrika",
      subtitle: "Sisi ni zaidi ya watazamaji. Sisi ni mashabiki tunaostahili kutambuliwa, kujumuishwa, na kupata nafasi mezani. Jiunge na harakati ili sauti za Kiafrika zisikike kwa klabu kubwa za mpira duniani.",
      cta: "Jibu Utafiti",
      ctaVision: "Maono Yetu",
      nations: "Mataifa ya Afrika",
      fans: "Mashabiki Watarajiwa",
      voice: "Sauti Moja"
    },
    why: {
      overline: "Kwa Nini Hii ni Muhimu",
      headline1: "Theluthi Moja ya Watazamaji wa Premier League",
      headline2: "ni Waafrika",
      p1: "Mpira wa Kiingereza una watazamaji wengi zaidi duniani, na mashabiki wa Kiafrika wanawakilisha sehemu kubwa. Hata hivyo, hatuwakilishwi vya kutosha katika mikakati ya ushirikishwaji.",
      p2: "Wengi wetu hatutawahi kuhudhuria mechi huko Anfield, Old Trafford, au Emirates, lakini uaminifu wetu na shauku yetu ni ya kweli kama mshahiki yeyote uwanjani.",
      p3: "Kampeni hii inalenga kuonyesha kuwa mashabiki wa Kiafrika ni sehemu muhimu ya utamaduni wa klabu za Premier League.",
      cta: "Ongeza Sauti Yako"
    },
    demands: {
      overline: "Mashabiki wa Kiafrika Wanataka Nini",
      headline: "Maono Yetu",
      subtitle: "Hizi ni maeneo muhimu ambapo mashabiki wa Kiafrika wanaona fursa kwa klabu kuimarisha uhusiano.",
      cta: "Shiriki Sauti Yako",
      items: [
        { title: "Ufikiaji wa Ndani wa Uzoefu wa Klabu", desc: "Mashabiki wa Kiafrika wanathamini fursa za kupata uzoefu wa klabu kupitia ziara na matukio." },
        { title: "Kutambuliwa kama Wafuasi wa Kimataifa", desc: "Mashabiki wanataka kuhisi kutambuliwa kama sehemu ya jumuiya ya klabu." },
        { title: "Ufikiaji wa Bidhaa Rasmi", desc: "Kuna mahitaji makubwa ya bidhaa halisi za klabu katika masoko ya ndani." },
        { title: "Kushiriki, Si Kutazama Tu", desc: "Mawasiliano ya njia moja hayatoshi tena. Mashabiki wanataka uhusiano wa pande mbili." },
        { title: "Thamani Zaidi ya Kutazama", desc: "Wafuasi wanataka uaminifu wao ubadilishwe kuwa uhusiano na faida za kweli." },
        { title: "Mifumo ya Uanachama Inayopatikana", desc: "Shauku wazi kwa chaguo za uanachama zilizoundwa kwa mashabiki wa kimataifa." },
        { title: "Chaguo za Malipo ya Ndani", desc: "Mashabiki wanahitaji njia za malipo zinazofaa masoko ya Kiafrika." },
        { title: "Mawasiliano ya Moja kwa Moja", desc: "Wafuasi wanathamini mawasiliano ya moja kwa moja na ya kibinafsi." },
        { title: "Jumuiya na Mitandao ya Mashabiki", desc: "Hamu kubwa ya jumuiya zilizopangwa zinazounganisha wafuasi." },
        { title: "Ufikiaji wa Kuaminika na Thabiti", desc: "Mashabiki wanataka njia rasmi na za kuaminika za kushirikiana na klabu." }
      ]
    },
    keynote: {
      title: "Ujumbe wa Heshima na Umoja",
      p1title: "Kama mashabiki wasio wakazi, tuna heshima kubwa kwa wafuasi wanaoshuhudia mechi.",
      p1: " Wao ndio moyo wa kweli wa klabu.",
      p2title: "Hatuko hapa kuchukua nafasi yao.",
      p2: " Tunataka tu kutambuliwa kama sehemu ya thamani ya jumuiya ya mashabiki.",
      p3title: "Tunaamini sana katika kuishi pamoja kwa amani.",
      p3: " Msaada wa mashabiki duniani kote kupitia haki za televisheni ulisaidia kueneza mpira kimataifa.",
      conclusion: "Tuna imani kwamba wakati umefika kuipeleka uhusiano huu katika kiwango kipya — uliojengwa juu ya heshima na ujumuishwaji wa kweli."
    },
    prize: { title: "Shinda Bidhaa za Kipekee", subtitle: "Washindi 100 watachaguliwa nasibu. Kamilisha utafiti na nambari ya simu kushiriki.", cta: "Jibu Utafiti", endsIn: "Inaisha:" },
    supportSection: { overline: "Wezesha Harakati", headline: "Unga Mkono Afro Fanthem", subtitle: "Kwa kufuatilia, kujisajili, na kuchangia, unaunga mkono mradi kifedha na kusaidia kukuza harakati." },
    finalCta: { headline1: "Uko Tayari", headline2: "Sauti Yako Isikike?", subtitle: "Jiunge na maelfu ya mashabiki wa Kiafrika katika mazungumzo na klabu za Premier League.", cta: "Jibu Utafiti" },
    footer: { tagline: "Tumeungana kwa Shauku, Tunaendeshwa na Kiburi", privacy: "Sera ya Faragha", about: "Kuhusu Sisi", rights: "Afro Fanthem. Haki zote zimehifadhiwa." },
    survey: {
      headline: "Sauti ya Mshahiki wa Kiafrika", subtitle: "Utafiti huu unawakilisha sauti ya mashabiki wa mpira wa Kiafrika duniani kote.",
      step: "Hatua", of: "ya", saving: "Inahifadhi...", previous: "Iliyotangulia", next: "Ifuatayo",
      submit: "Wasilisha Utafiti", completeRegister: "Kamilisha na Sajili",
      prizeTitle: "Kushiriki Bahati Nasibu", prizeSubtitle: "Si lazima - Shiriki kushinda bidhaa",
      prizeInfo: "Mwishoni mwa Agosti, tutachagua nasibu mashabiki 100 kushinda bidhaa za klabu yao. Nambari ya simu inahitajika.",
      prizeQ: "Je, ungependa kushiriki bahati nasibu?", prizeYes: "Ndiyo, nataka kushiriki", prizeNo: "Hapana, ruka",
      emailPrize: "Barua pepe", phonePrize: "Nambari ya Simu", phoneNote: "Jumuisha nambari ya nchi.",
      fanProfile: "Wasifu wa Mshahiki", country: "Nchi", city: "Jiji", ageGroup: "Kundi la Umri", followFreq: "Mara ngapi unafuatilia klabu yako?",
      clubSupport: "Msaada wa Klabu", clubQ: "Klabu gani unaunga mkono?", clubOther: "Taja klabu yako", duration: "Umeunga mkono klabu hii kwa muda gani?",
      engagement: "Ushirikishwaji wa Leo", engageQ: "Unashirikiana vipi na klabu yako?", selectAll: "Chagua zote zinazofaa",
      recognised: "Je, unahisi kutambuliwa na klabu yako?", doEnough: "Je, klabu zinafanya ya kutosha?",
      connection: "Unahisi uhusiano kiasi gani na klabu yako?",
      gap: "Pengo", gapQ: "Nini kinachokuzuia kushirikiana zaidi?",
      future: "Fursa ya Baadaye", memberQ: "Klabu yako ikitolea uanachama wa kimataifa, ungependa?",
      memberValues: "Ungethamini nini zaidi?", selectUp: "Chagua hadi 3", selected: "zimechaguliwa",
      priceQ: "Ungekuwa tayari kulipa kiasi gani kwa mwezi?",
      voice: "Sauti Yako", voiceIntro: "Hii ni fursa yako ya kuzungumza moja kwa moja na klabu.",
      voiceQ: "Kwa maneno yako, klabu yako inaweza kufanya nini vizuri zaidi?",
      voiceNote: "Si lazima, lakini sauti yako ni muhimu!", winners: "Washindi 100 Watachaguliwa!"
    },
    register: {
      finalStep: "Hatua ya Mwisho", step1: "Hatua 1 ya 2",
      headlineValidate: "Thibitisha Sauti Yako", headlineJoin: "Jiunge na Harakati",
      subtitleValidate: "Sajili barua pepe yako kuthibitisha majibu yako ya utafiti.",
      subtitleJoin: "Sajili kuongeza sauti yako. Baada ya kusajili, utakamilisha utafiti mfupi.",
      trust: "Tunathibitisha kila jibu kuhakikisha sauti halisi za mashabiki wa Kiafrika zinasikika.", whyVerify: "Kwa nini tunathibitisha:",
      email: "Barua Pepe", phone: "Nambari ya Simu", phoneNote: "Nambari ya simu inahitajika kwa bahati nasibu.",
      prizeInfo: "Mashabiki 100 watachaguliwa mwishoni mwa Agosti kushinda bidhaa.", prizeTitle: "Shinda Bidhaa!",
      consent: "Ninakubali kuhifadhi na kusindika data yangu kwa madhumuni ya kampeni hii.",
      privacyLink: "Tazama Sera ya Faragha", submitValidate: "Thibitisha na Wasilisha", submitContinue: "Endelea kwa Utafiti",
      sending: "Inatuma uthibitisho...", registering: "Inasajili...", already: "Umeshasajiliwa?", returnHome: "Rudi nyumbani",
      optional: "(si lazima)", required: "*",
      referralBanner: "Umealikwa na mshahiki mwenzako!",
      checkEmail: "Angalia Barua Pepe Yako", checkEmailMsg: "Bonyeza kiungo katika barua pepe kuthibitisha anwani yako.",
      didntReceive: "Hukupokea barua pepe? Angalia folda ya barua taka au", tryAgain: "jaribu tena na barua pepe nyingine"
    },
    verify: {
      verifying: "Inathibitisha barua pepe yako...", failed: "Uthibitisho Umeshindikana", returnHome: "Rudi Nyumbani",
      alreadyVerified: "Tayari Imethibitishwa!", verified: "Barua Pepe Imethibitishwa!",
      subtitle: "Sauti yako imethibitishwa na kuongezwa kwa harakati.", subtitle2: "Pamoja, tunajega hoja ya uwakilishi.",
      spread: "Eneza Neno", spreadSub: "Kila sauti inaoimarisha harakati!",
      whatsapp: "Shiriki kwenye WhatsApp", shareOther: "Shiriki kwa Njia Nyingine",
      earnTitle: "Pata Nafasi za Ziada", earnSub: "Shiriki kiungo chako. Marafiki wakikamilisha utafiti, nyote mnapata nafasi ya ziada!",
      copy: "Nakili", backHome: "Rudi Nyumbani"
    },
    about: {
      headline: "Kuhusu", headlineHighlight: "Afro Fanthem", takeSurvey: "Jibu Utafiti", home: "Nyumbani",
      s1Title: "Kuhusu Afro Fanthem",
      s1p1: "Afro Fanthem ni mkusanyiko wa mashabiki wa Kiafrika, wanaofanya kazi pamoja na kushirikiana na majukwaa ya ushirikishwaji wa mashabiki kujenga uhusiano wenye nguvu zaidi.",
      s1p2: "Tunawakilisha mashabiki wanaopenda mchezo kwa undani, hata wakiwa mbali nao.",
      s2Title: "Kwa Nini Tupo", s2p1: "Kote Afrika, mamilioni ya mashabiki wanajitokeza kila wiki.",
      s2p2: "Tunaamka mapema. Tunakesha usiku. Tunasherehekea kila goli na kubeba kila hasara.",
      s2p3: "Lakini kwa wengi wetu, ukweli ni rahisi. Hatutawahi kukanyaga uwanjani. Si kwa sababu hatujali. Bali kwa sababu ya umbali, gharama, na ufikiaji.",
      s2p4: "Na kwa sababu hiyo, mara nyingi tunajisikia kama hatuhusiki kikamilifu.", s2p5: "Afro Fanthem ipo kubadilisha hilo.",
      s3Title: "Kutambuliwa Kunamaanisha Nini",
      s3p1: "Kutambuliwa si tu kuonekana. Ni kujumuishwa kwa njia ya kweli.",
      s3p2: "Inamaanisha ufikiaji wa bidhaa bila vikwazo. Inamaanisha kuhesabiwa wakati maamuzi yanafanywa.",
      s3p3: "Inamaanisha uanachama wa kimataifa wenye maana kwa mashabiki ambao hawatafika uwanjani, lakini bado ni sehemu ya klabu kila siku.",
      s3p4: "Inamaanisha kujua kwamba umbali haukufanyi kuwa mshahiki mdogo.",
      s4Title: "Tunachokijenga", s4p1: "Tunajenga sauti moja ya mashabiki wa Kiafrika. Si kuchukua nafasi ya mtu yeyote. Bali kusimama kando ya mashabiki kila mahali.",
      s4p2: "Kwa sababu hii si kuhusu kuchukua nafasi ya wengine. Ni kuhakikisha hatubaki nje.",
      s5Title: "Jinsi Unavyoweza Kushiriki", s5p1: "Njia rahisi zaidi ya kuwa sehemu ya hii ni kufanya sauti yako ihesabike.",
      s5p2: "Jaza utafiti. Ushiriki na jumuiya yako.", s5p3: "Kila jibu ni muhimu. Kila sauti inaongeza nguvu.",
      s6Title: "Lengo", s6p1: "Kufikia msimu wa 2026/27, tunataka mashabiki wa Kiafrika watambuliwe kama sehemu muhimu ya mazungumzo ya mashabiki duniani.",
      s6p2: "Si tu kuwepo. Bali kujumuishwa. Kuthaminiwa. Kusikika.",
      joinHeadline: "Jiunge na Harakati",
      joinP1: "Ikiwa umewahi kuunga mkono klabu kutoka mbali", joinP2: "Ikiwa umewahi kuhisi kupuuzwa kama mshahiki",
      joinP3: "Ikiwa umewahi kujiuliza kama sauti yako ni muhimu", joinCta: "Hii ni kwa ajili yako.", joinButton: "Jibu Utafiti"
    },
    thankyou: {
      headline: "Asante!", subtitle: "Sauti yako imeongezwa kwa harakati.", subtitle2: "Pamoja tunajega hoja ya uwakilishi.",
      prizeTitle: "Uko kwenye Bahati Nasibu!", earnTitle: "Pata Nafasi za Ziada",
      earnSub: "Shiriki kiungo chako na marafiki.",
      supportTitle: "Wezesha Harakati", supportSub: "Kwa kufuatilia na kuchangia, unaunga mkono mradi.",
      sharePrompt: "Tusaidie kueneza neno!", whatsapp: "Shiriki kwenye WhatsApp", shareOther: "Shiriki kwa Njia Nyingine", backHome: "Rudi Nyumbani"
    }
  },
  fr: {
    nav: { vision: "Notre Vision", why: "Pourquoi C'est Important", about: "À Propos", support: "Nous Soutenir", survey: "Répondre au Sondage", home: "Accueil" },
    hero: {
      overline: "Unis par la Passion, Portés par la Fierté",
      headline1: "La Voix des",
      headline2: "Supporters Africains de Football",
      subtitle: "Nous sommes plus que des téléspectateurs. Nous sommes des supporters qui méritent la reconnaissance, l'inclusion et une place à la table. Rejoignez le mouvement pour faire entendre les voix africaines.",
      cta: "Répondre au Sondage",
      ctaVision: "Notre Vision",
      nations: "Nations Africaines",
      fans: "Supporters Potentiels",
      voice: "Voix Unie"
    },
    why: {
      overline: "Pourquoi C'est Important",
      headline1: "Un Tiers des Téléspectateurs de Premier League",
      headline2: "Sont Africains",
      p1: "Le football anglais a la plus grande audience mondiale, et les supporters africains représentent une part importante de ce public. Pourtant, nous restons sous-représentés dans les stratégies d'engagement des clubs.",
      p2: "Beaucoup d'entre nous n'assisteront jamais à un match à Anfield, Old Trafford ou l'Emirates, mais notre loyauté et notre passion sont aussi réelles que celles de tout supporter dans les tribunes.",
      p3: "Cette campagne vise à démontrer que les supporters africains font partie intégrante de la culture des clubs de Premier League.",
      cta: "Ajoutez Votre Voix"
    },
    demands: {
      overline: "Ce Que Veulent les Supporters Africains",
      headline: "Notre Vision",
      subtitle: "Voici les domaines clés où les supporters africains voient des opportunités pour les clubs d'approfondir l'engagement.",
      cta: "Partagez Votre Voix",
      items: [
        { title: "Accès Local aux Expériences de Club", desc: "Les supporters africains apprécient les opportunités de vivre les clubs à travers des tournées et des événements." },
        { title: "Reconnaissance en tant que Supporters Mondiaux", desc: "Les fans veulent être reconnus comme faisant partie de la communauté mondiale du club." },
        { title: "Accès aux Produits Officiels", desc: "Forte demande de merchandising authentique accessible dans les marchés locaux." },
        { title: "Participation, Pas Seulement Visionnage", desc: "La communication à sens unique ne suffit plus. Les fans veulent une relation bidirectionnelle." },
        { title: "Valeur Au-delà du Visionnage", desc: "Les supporters veulent que leur loyauté se traduise en connexion et avantages réels." },
        { title: "Modèles d'Adhésion Accessibles", desc: "Intérêt clair pour des options d'adhésion adaptées aux supporters internationaux." },
        { title: "Options de Paiement Localisées", desc: "Les fans ont besoin de méthodes de paiement adaptées aux marchés africains." },
        { title: "Communication Directe et Significative", desc: "Les supporters apprécient une communication directe et personnalisée." },
        { title: "Communauté et Réseaux de Fans", desc: "Fort appétit pour des communautés organisées connectant les supporters." },
        { title: "Accès Fiable et Constant", desc: "Les fans veulent des canaux officiels fiables pour interagir avec les clubs." }
      ]
    },
    keynote: {
      title: "Un Message de Respect et d'Unité",
      p1title: "En tant que supporters non-résidents, nous avons un grand respect pour les supporters présents aux matchs.",
      p1: " Ils sont le véritable cœur du club.",
      p2title: "Nous ne sommes pas là pour les remplacer.",
      p2: " Nous voulons simplement être reconnus comme une partie précieuse de la communauté de supporters.",
      p3title: "Nous croyons fermement en la coexistence harmonieuse.",
      p3: " Le soutien des fans à travers le monde via les droits télévisés a aidé à diffuser le football mondialement.",
      conclusion: "Nous sommes convaincus que le moment est venu de porter cette relation au niveau supérieur — fondée sur le respect mutuel et l'inclusion véritable."
    },
    prize: { title: "Gagnez du Merchandising Exclusif", subtitle: "100 gagnants sélectionnés au hasard. Complétez le sondage avec un numéro de téléphone pour participer.", cta: "Répondre au Sondage", endsIn: "Se termine dans :" },
    supportSection: { overline: "Alimentez le Mouvement", headline: "Soutenez Afro Fanthem", subtitle: "En suivant, en vous abonnant et en faisant un don, vous soutenez directement le projet." },
    finalCta: { headline1: "Prêt à Faire", headline2: "Entendre Votre Voix ?", subtitle: "Rejoignez des milliers de supporters africains dans la conversation avec les clubs de Premier League.", cta: "Répondre au Sondage" },
    footer: { tagline: "Unis par la Passion, Portés par la Fierté", privacy: "Politique de Confidentialité", about: "À Propos", rights: "Afro Fanthem. Tous droits réservés." },
    survey: {
      headline: "La Voix du Supporter Africain", subtitle: "Ce sondage représente la voix des supporters africains dans le monde.",
      step: "Étape", of: "sur", saving: "Sauvegarde...", previous: "Précédent", next: "Suivant",
      submit: "Soumettre le Sondage", completeRegister: "Compléter et S'inscrire",
      prizeTitle: "Participation au Tirage", prizeSubtitle: "Optionnel - Participez pour gagner du merchandising",
      prizeInfo: "Fin août, 100 supporters seront choisis au hasard pour gagner du merchandising de leur club. Un numéro de téléphone est requis.",
      prizeQ: "Souhaitez-vous participer au tirage ?", prizeYes: "Oui, je veux participer", prizeNo: "Non, passer",
      emailPrize: "Email pour le Tirage", phonePrize: "Numéro de Téléphone", phoneNote: "Incluez l'indicatif pays.",
      fanProfile: "Profil du Supporter", country: "Pays", city: "Ville", ageGroup: "Groupe d'Âge", followFreq: "À quelle fréquence suivez-vous votre club ?",
      clubSupport: "Soutien au Club", clubQ: "Quel club soutenez-vous ?", clubOther: "Précisez votre club", duration: "Depuis combien de temps soutenez-vous ce club ?",
      engagement: "Engagement Actuel", engageQ: "Comment vous engagez-vous avec votre club ?", selectAll: "Sélectionnez tout ce qui s'applique",
      recognised: "Vous sentez-vous reconnu par votre club ?", doEnough: "Les clubs en font-ils assez ?",
      connection: "Quel est votre niveau de connexion avec votre club ?",
      gap: "Le Fossé", gapQ: "Qu'est-ce qui vous empêche de vous engager davantage ?",
      future: "Opportunité Future", memberQ: "Si votre club offrait une adhésion internationale, seriez-vous intéressé ?",
      memberValues: "Qu'est-ce qui aurait le plus de valeur ?", selectUp: "Sélectionnez jusqu'à 3", selected: "sélectionnés",
      priceQ: "Combien seriez-vous prêt à payer par mois ?",
      voice: "Votre Voix", voiceIntro: "C'est votre chance de parler directement aux clubs.",
      voiceQ: "Que pourrait faire votre club pour vous sentir plus connecté ?",
      voiceNote: "Optionnel, mais votre voix compte !", winners: "100 Gagnants Seront Sélectionnés !"
    },
    register: {
      finalStep: "Dernière Étape", step1: "Étape 1 sur 2",
      headlineValidate: "Validez Votre Voix", headlineJoin: "Rejoignez le Mouvement",
      subtitleValidate: "Inscrivez votre email pour valider votre réponse au sondage.",
      subtitleJoin: "Inscrivez-vous pour ajouter votre voix. Après l'inscription, vous compléterez un court sondage.",
      trust: "Nous validons chaque réponse pour assurer l'authenticité des voix africaines.", whyVerify: "Pourquoi nous vérifions :",
      email: "Adresse Email", phone: "Numéro de Téléphone", phoneNote: "Le numéro est requis pour le tirage.",
      prizeInfo: "100 supporters seront sélectionnés fin août pour gagner du merchandising.", prizeTitle: "Gagnez du Merchandising !",
      consent: "J'accepte le stockage et le traitement de mes données pour cette campagne.",
      privacyLink: "Voir la Politique de Confidentialité", submitValidate: "Vérifier et Soumettre", submitContinue: "Continuer vers le Sondage",
      sending: "Envoi de la vérification...", registering: "Inscription...", already: "Déjà inscrit ?", returnHome: "Retour à l'accueil",
      optional: "(optionnel)", required: "*",
      referralBanner: "Vous avez été invité par un supporter !",
      checkEmail: "Vérifiez Votre Email", checkEmailMsg: "Cliquez sur le lien dans l'email pour vérifier votre adresse.",
      didntReceive: "Pas reçu l'email ? Vérifiez vos spams ou", tryAgain: "réessayez avec un autre email"
    },
    verify: {
      verifying: "Vérification de votre email...", failed: "Échec de la Vérification", returnHome: "Retour à l'Accueil",
      alreadyVerified: "Déjà Vérifié !", verified: "Email Vérifié !",
      subtitle: "Votre voix a été validée.", subtitle2: "Ensemble, nous construisons un dossier crédible.",
      spread: "Partagez", spreadSub: "Chaque voix renforce le mouvement !",
      whatsapp: "Partager sur WhatsApp", shareOther: "Partager autrement",
      earnTitle: "Gagnez des Chances Supplémentaires", earnSub: "Partagez votre lien. Quand vos amis complètent le sondage, vous gagnez tous les deux !",
      copy: "Copier", backHome: "Retour à l'Accueil"
    },
    about: {
      headline: "À Propos d'", headlineHighlight: "Afro Fanthem", takeSurvey: "Répondre au Sondage", home: "Accueil",
      s1Title: "À Propos d'Afro Fanthem",
      s1p1: "Afro Fanthem est un collectif de supporters africains, travaillant ensemble et collaborant avec des plateformes d'engagement pour construire des relations plus fortes et inclusives entre les clubs et les supporters.",
      s1p2: "Nous représentons des supporters qui aiment profondément le jeu, même lorsqu'ils en sont loin.",
      s2Title: "Pourquoi Nous Existons", s2p1: "Partout en Afrique, des millions de supporters se mobilisent chaque semaine.",
      s2p2: "Nous nous levons tôt. Nous veillons tard. Nous célébrons chaque but et portons chaque défaite.",
      s2p3: "Mais pour la plupart d'entre nous, la réalité est simple. Nous ne mettrons jamais les pieds dans un stade. Pas parce que nous ne nous soucions pas assez. Mais à cause de la distance, du coût et de l'accès.",
      s2p4: "Et à cause de cela, on nous fait souvent sentir que nous n'appartenons pas pleinement.", s2p5: "Afro Fanthem existe pour changer cela.",
      s3Title: "Ce Que Signifie la Reconnaissance",
      s3p1: "La reconnaissance ne se résume pas à être vu. C'est être inclus de manière réelle.",
      s3p2: "Cela signifie l'accès aux produits sans barrières. Cela signifie être compté quand les décisions sont prises.",
      s3p3: "Cela signifie une adhésion internationale significative pour les supporters qui n'atteindront peut-être jamais le stade.",
      s3p4: "Cela signifie savoir que la distance ne fait pas de vous un supporter moindre.",
      s4Title: "Ce Que Nous Construisons", s4p1: "Nous construisons une voix africaine unie. Pas pour remplacer quiconque. Mais pour se tenir aux côtés des supporters partout.",
      s4p2: "Car il ne s'agit pas de prendre la place des autres. Il s'agit de s'assurer que nous n'en sommes pas exclus.",
      s5Title: "Comment Vous Pouvez Participer", s5p1: "La façon la plus simple d'en faire partie est de faire compter votre voix.",
      s5p2: "Remplissez le sondage. Partagez-le avec votre communauté.", s5p3: "Chaque réponse compte. Chaque voix ajoute de la force.",
      s6Title: "L'Objectif", s6p1: "D'ici la saison 2026/27, nous voulons que les supporters africains soient reconnus comme une partie essentielle de la conversation mondiale.",
      s6p2: "Pas seulement présents. Mais inclus. Valorisés. Entendus.",
      joinHeadline: "Rejoignez le Mouvement",
      joinP1: "Si vous avez déjà soutenu un club de loin", joinP2: "Si vous vous êtes déjà senti ignoré en tant que supporter",
      joinP3: "Si vous vous êtes déjà demandé si votre voix compte", joinCta: "C'est pour vous.", joinButton: "Répondre au Sondage"
    },
    thankyou: {
      headline: "Merci !", subtitle: "Votre voix a été ajoutée au mouvement.", subtitle2: "Ensemble nous construisons un dossier crédible.",
      prizeTitle: "Vous Participez au Tirage !", earnTitle: "Gagnez des Chances Supplémentaires",
      earnSub: "Partagez votre lien avec vos amis.",
      supportTitle: "Alimentez le Mouvement", supportSub: "En suivant et en faisant un don, vous soutenez le projet.",
      sharePrompt: "Aidez-nous à faire passer le mot !", whatsapp: "Partager sur WhatsApp", shareOther: "Partager autrement", backHome: "Retour à l'Accueil"
    }
  },
  pt: {
    nav: { vision: "Nossa Visão", why: "Por Que Isto Importa", about: "Sobre Nós", support: "Apoie-nos", survey: "Responda a Pesquisa", home: "Início" },
    hero: {
      overline: "Unidos pela Paixão, Movidos pelo Orgulho",
      headline1: "A Voz dos",
      headline2: "Torcedores Africanos de Futebol",
      subtitle: "Somos mais do que espectadores. Somos torcedores que merecem reconhecimento, inclusão e um lugar à mesa. Junte-se ao movimento para fazer as vozes africanas serem ouvidas.",
      cta: "Responda a Pesquisa",
      ctaVision: "Nossa Visão",
      nations: "Nações Africanas",
      fans: "Torcedores Potenciais",
      voice: "Voz Unida"
    },
    why: {
      overline: "Por Que Isto Importa",
      headline1: "Um Terço dos Espectadores da Premier League",
      headline2: "São Africanos",
      p1: "O futebol inglês tem a maior audiência global, e os torcedores africanos representam uma parte significativa. No entanto, permanecemos sub-representados nas estratégias de engajamento dos clubes.",
      p2: "Muitos de nós nunca assistirão a um jogo em Anfield, Old Trafford ou Emirates, mas nossa lealdade e paixão são tão reais quanto as de qualquer torcedor nas arquibancadas.",
      p3: "Esta campanha visa demonstrar que os torcedores africanos são parte integrante da cultura dos clubes da Premier League.",
      cta: "Adicione Sua Voz"
    },
    demands: {
      overline: "O Que os Torcedores Africanos Querem",
      headline: "Nossa Visão",
      subtitle: "Estas são as áreas-chave onde os torcedores africanos veem oportunidades para os clubes aprofundarem o engajamento.",
      cta: "Compartilhe Sua Voz",
      items: [
        { title: "Acesso Local a Experiências do Clube", desc: "Torcedores africanos valorizam oportunidades de experienciar os clubes através de tours e eventos." },
        { title: "Reconhecimento como Torcedores Globais", desc: "Os fãs querem ser reconhecidos como parte da comunidade global do clube." },
        { title: "Acesso a Produtos Oficiais", desc: "Forte demanda por merchandising autêntico e acessível nos mercados locais." },
        { title: "Participação, Não Apenas Audiência", desc: "Comunicação unidirecional não é mais suficiente. Os fãs querem uma relação bidirecional." },
        { title: "Valor Além da Audiência", desc: "Os torcedores querem que sua lealdade se traduza em conexão e benefícios reais." },
        { title: "Modelos de Associação Acessíveis", desc: "Interesse claro em opções de associação adaptadas para torcedores internacionais." },
        { title: "Opções de Pagamento Localizadas", desc: "Os fãs precisam de métodos de pagamento adequados aos mercados africanos." },
        { title: "Comunicação Direta e Significativa", desc: "Os torcedores valorizam comunicação direta e personalizada." },
        { title: "Comunidade e Redes de Fãs", desc: "Forte apetite por comunidades organizadas que conectam torcedores." },
        { title: "Acesso Confiável e Consistente", desc: "Os fãs querem canais oficiais confiáveis para interagir com os clubes." }
      ]
    },
    keynote: {
      title: "Uma Mensagem de Respeito e Unidade",
      p1title: "Como torcedores não residentes, temos grande respeito pelos torcedores que vão aos jogos.",
      p1: " Eles são o verdadeiro coração do clube.",
      p2title: "Não estamos aqui para substituí-los.",
      p2: " Simplesmente queremos ser reconhecidos como parte valiosa da comunidade de torcedores.",
      p3title: "Acreditamos fortemente na coexistência harmoniosa.",
      p3: " O apoio dos fãs ao redor do mundo através dos direitos de TV ajudou a espalhar o futebol globalmente.",
      conclusion: "Estamos confiantes de que chegou o momento de levar esta relação ao próximo nível — construída sobre respeito mútuo e inclusão genuína."
    },
    prize: { title: "Ganhe Merchandising Exclusivo", subtitle: "100 ganhadores selecionados aleatoriamente. Complete a pesquisa com número de telefone para participar.", cta: "Responda a Pesquisa", endsIn: "Termina em:" },
    supportSection: { overline: "Alimente o Movimento", headline: "Apoie o Afro Fanthem", subtitle: "Seguindo, se inscrevendo e doando, você apoia diretamente o projeto." },
    finalCta: { headline1: "Pronto para Fazer", headline2: "Sua Voz Ser Ouvida?", subtitle: "Junte-se a milhares de torcedores africanos na conversa com os clubes da Premier League.", cta: "Responda a Pesquisa" },
    footer: { tagline: "Unidos pela Paixão, Movidos pelo Orgulho", privacy: "Política de Privacidade", about: "Sobre Nós", rights: "Afro Fanthem. Todos os direitos reservados." },
    survey: {
      headline: "A Voz do Torcedor Africano", subtitle: "Esta pesquisa representa a voz dos torcedores africanos de futebol globalmente.",
      step: "Etapa", of: "de", saving: "Salvando...", previous: "Anterior", next: "Próximo",
      submit: "Enviar Pesquisa", completeRegister: "Completar e Registrar",
      prizeTitle: "Participação no Sorteio", prizeSubtitle: "Opcional - Participe para ganhar merchandising",
      prizeInfo: "No final de agosto, selecionaremos aleatoriamente 100 torcedores para ganhar merchandising do seu clube. Número de telefone é obrigatório.",
      prizeQ: "Gostaria de participar do sorteio?", prizeYes: "Sim, quero participar", prizeNo: "Não, pular",
      emailPrize: "Email para o Sorteio", phonePrize: "Número de Telefone", phoneNote: "Inclua o código do país.",
      fanProfile: "Perfil do Torcedor", country: "País", city: "Cidade", ageGroup: "Faixa Etária", followFreq: "Com que frequência você acompanha seu clube?",
      clubSupport: "Apoio ao Clube", clubQ: "Qual clube você torce?", clubOther: "Especifique seu clube", duration: "Há quanto tempo você torce para este clube?",
      engagement: "Engajamento Atual", engageQ: "Como você se engaja com seu clube?", selectAll: "Selecione todos que se aplicam",
      recognised: "Você se sente reconhecido pelo seu clube?", doEnough: "Os clubes fazem o suficiente?",
      connection: "Quão conectado você se sente ao seu clube?",
      gap: "A Lacuna", gapQ: "O que impede você de se engajar mais?",
      future: "Oportunidade Futura", memberQ: "Se seu clube oferecesse uma associação internacional, você estaria interessado?",
      memberValues: "O que você mais valorizaria?", selectUp: "Selecione até 3", selected: "selecionados",
      priceQ: "Quanto você estaria disposto a pagar por mês?",
      voice: "Sua Voz", voiceIntro: "Esta é sua chance de falar diretamente com os clubes.",
      voiceQ: "Com suas palavras, o que seu clube poderia fazer melhor?",
      voiceNote: "Opcional, mas sua voz importa!", winners: "100 Ganhadores Serão Selecionados!"
    },
    register: {
      finalStep: "Etapa Final", step1: "Etapa 1 de 2",
      headlineValidate: "Valide Sua Voz", headlineJoin: "Junte-se ao Movimento",
      subtitleValidate: "Registre seu email para validar sua resposta à pesquisa.",
      subtitleJoin: "Registre-se para adicionar sua voz. Após o registro, você completará uma pesquisa curta.",
      trust: "Validamos cada resposta para garantir autenticidade das vozes africanas.", whyVerify: "Por que verificamos:",
      email: "Endereço de Email", phone: "Número de Telefone", phoneNote: "Número de telefone é necessário para o sorteio.",
      prizeInfo: "100 torcedores serão selecionados no final de agosto para ganhar merchandising.", prizeTitle: "Ganhe Merchandising!",
      consent: "Concordo com o armazenamento e processamento dos meus dados para esta campanha.",
      privacyLink: "Ver Política de Privacidade", submitValidate: "Verificar e Enviar", submitContinue: "Continuar para a Pesquisa",
      sending: "Enviando verificação...", registering: "Registrando...", already: "Já registrado?", returnHome: "Voltar ao início",
      optional: "(opcional)", required: "*",
      referralBanner: "Você foi convidado por outro torcedor!",
      checkEmail: "Verifique Seu Email", checkEmailMsg: "Clique no link do email para verificar seu endereço.",
      didntReceive: "Não recebeu o email? Verifique a pasta de spam ou", tryAgain: "tente novamente com outro email"
    },
    verify: {
      verifying: "Verificando seu email...", failed: "Falha na Verificação", returnHome: "Voltar ao Início",
      alreadyVerified: "Já Verificado!", verified: "Email Verificado!",
      subtitle: "Sua voz foi validada e adicionada ao movimento.", subtitle2: "Juntos, estamos construindo um caso crível.",
      spread: "Espalhe a Palavra", spreadSub: "Cada voz fortalece o movimento!",
      whatsapp: "Compartilhar no WhatsApp", shareOther: "Compartilhar de Outra Forma",
      earnTitle: "Ganhe Chances Extras", earnSub: "Compartilhe seu link. Quando amigos completam a pesquisa, vocês dois ganham!",
      copy: "Copiar", backHome: "Voltar ao Início"
    },
    about: {
      headline: "Sobre o", headlineHighlight: "Afro Fanthem", takeSurvey: "Responda a Pesquisa", home: "Início",
      s1Title: "Sobre o Afro Fanthem",
      s1p1: "Afro Fanthem é um coletivo de torcedores africanos, trabalhando juntos e colaborando com plataformas de engajamento para construir relacionamentos mais fortes e inclusivos entre clubes e torcedores.",
      s1p2: "Representamos torcedores que amam profundamente o jogo, mesmo quando estão longe dele.",
      s2Title: "Por Que Existimos", s2p1: "Em toda a África, milhões de torcedores aparecem toda semana.",
      s2p2: "Acordamos cedo. Ficamos acordados até tarde. Celebramos cada gol e carregamos cada derrota.",
      s2p3: "Mas para a maioria de nós, a realidade é simples. Nunca pisaremos em um estádio. Não porque não nos importamos. Mas por causa da distância, custo e acesso.",
      s2p4: "E por isso, muitas vezes nos fazem sentir que não pertencemos totalmente.", s2p5: "O Afro Fanthem existe para mudar isso.",
      s3Title: "O Que Reconhecimento Significa",
      s3p1: "Reconhecimento não é apenas ser visto. Significa ser incluído de forma real.",
      s3p2: "Significa acesso a produtos sem barreiras. Significa ser contado quando decisões são tomadas.",
      s3p3: "Significa associação internacional significativa para torcedores que podem nunca chegar ao estádio.",
      s3p4: "Significa saber que a distância não faz de você um torcedor menor.",
      s4Title: "O Que Estamos Construindo", s4p1: "Estamos construindo uma voz africana unificada. Não para substituir ninguém. Mas para estar ao lado de torcedores em todos os lugares.",
      s4p2: "Porque não se trata de tomar espaço dos outros. Trata-se de garantir que não sejamos deixados de fora.",
      s5Title: "Como Você Pode Participar", s5p1: "A maneira mais simples de fazer parte é fazer sua voz contar.",
      s5p2: "Preencha a pesquisa. Compartilhe com sua comunidade.", s5p3: "Cada resposta importa. Cada voz adiciona força.",
      s6Title: "O Objetivo", s6p1: "Até a temporada 2026/27, queremos que os torcedores africanos sejam reconhecidos como parte crítica da conversa global.",
      s6p2: "Não apenas presentes. Mas incluídos. Valorizados. Ouvidos.",
      joinHeadline: "Junte-se ao Movimento",
      joinP1: "Se você já torceu para um clube de longe", joinP2: "Se você já se sentiu ignorado como torcedor",
      joinP3: "Se você já se perguntou se sua voz importa", joinCta: "Isto é para você.", joinButton: "Responda a Pesquisa"
    },
    thankyou: {
      headline: "Obrigado!", subtitle: "Sua voz foi adicionada ao movimento.", subtitle2: "Juntos construímos um caso crível.",
      prizeTitle: "Você Está no Sorteio!", earnTitle: "Ganhe Chances Extras",
      earnSub: "Compartilhe seu link com amigos.",
      supportTitle: "Alimente o Movimento", supportSub: "Seguindo e doando, você apoia o projeto.",
      sharePrompt: "Ajude-nos a espalhar a palavra!", whatsapp: "Compartilhar no WhatsApp", shareOther: "Compartilhar de Outra Forma", backHome: "Voltar ao Início"
    }
  }
};
